(() => {
  const roots = document.querySelectorAll('[data-coa-lookup]');
  if (!roots.length) return;

  const normalize = (value) => String(value || '').toLowerCase().trim();
  const formatDate = (value) => value || '';

  roots.forEach((root) => {
    const indexNode = root.querySelector('[data-coa-index]');
    let lots = [];

    try {
      lots = JSON.parse(indexNode?.textContent || '[]');
    } catch (error) {
      lots = [];
      console.error('ELIXIR COA lookup index could not be parsed.', error);
    }

    const input = root.querySelector('[data-coa-search]');
    const suggestions = root.querySelector('[data-coa-suggestions]');
    const statusMessage = root.querySelector('[data-coa-status-message]');
    const emptyState = root.querySelector('[data-coa-empty]');
    const selectedPanel = root.querySelector('[data-coa-selected]');
    const titleNode = root.querySelector('[data-coa-title]');
    const detailsList = root.querySelector('[data-coa-details-list]');
    const documentsList = root.querySelector('[data-coa-documents-list]');
    const documentsEmpty = root.querySelector('[data-coa-documents-empty]');
    const relatedList = root.querySelector('[data-coa-related-list]');
    const relatedEmpty = root.querySelector('[data-coa-related-empty]');

    let matches = [];
    let activeIndex = -1;
    let selectedId = null;

    const clearNode = (node) => {
      while (node && node.firstChild) node.removeChild(node.firstChild);
    };

    const text = (value) => document.createTextNode(value || '');

    const setMessage = (message) => {
      if (statusMessage) statusMessage.textContent = message || '';
    };

    const closeSuggestions = () => {
      suggestions?.classList.remove('is-open');
      suggestions?.setAttribute('aria-expanded', 'false');
      input?.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
    };

    const searchLots = (query) => {
      const q = normalize(query);
      if (!q) return [];
      return lots.filter((lot) => normalize(lot.searchText).includes(q)).slice(0, 12);
    };

    const setActiveSuggestion = (nextIndex) => {
      const buttons = Array.from(suggestions?.querySelectorAll('button') || []);
      buttons.forEach((button, index) => {
        const isActive = index === nextIndex;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) {
          input?.setAttribute('aria-activedescendant', button.id);
          button.scrollIntoView({ block: 'nearest' });
        }
      });
      if (nextIndex < 0) input?.removeAttribute('aria-activedescendant');
      activeIndex = nextIndex;
    };

    const renderSuggestions = () => {
      clearNode(suggestions);
      if (!matches.length) {
        closeSuggestions();
        if (input?.value.trim()) setMessage('No matching lot records found. Check the lot number and try again, or search by product name.');
        return;
      }

      const fragment = document.createDocumentFragment();
      matches.forEach((lot, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'coa-lookup__suggestion';
        button.id = `${root.id || 'coa-lookup'}-suggestion-${index}`;
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', 'false');
        button.dataset.lotId = lot.id;

        const title = document.createElement('span');
        title.className = 'coa-lookup__suggestion-title';
        title.append(text([lot.productTitle, lot.variantTitle].filter(Boolean).join(' — ')));

        const meta = document.createElement('span');
        meta.className = 'coa-lookup__suggestion-meta';
        const parts = [`Lot ${lot.lotNumber}`, lot.status, formatDate(lot.releaseDate)].filter(Boolean);
        meta.append(text(parts.join(' · ')));

        button.append(title, meta);
        button.addEventListener('click', () => selectLot(lot.id, true));
        fragment.append(button);
      });
      suggestions?.append(fragment);
      suggestions?.classList.add('is-open');
      suggestions?.setAttribute('aria-expanded', 'true');
      input?.setAttribute('aria-expanded', 'true');
      setMessage(`${matches.length} matching lot ${matches.length === 1 ? 'record' : 'records'} available.`);
    };

    const fieldRows = (lot) => [
      ['Product', lot.productTitle],
      ['Variant / mass', lot.variantTitle],
      ['Lot number', lot.lotNumber],
      ['Barcode ID', lot.barcodeId],
      ['Status', lot.status],
      ['Manufactured date', lot.manufacturedDate],
      ['Release date', lot.releaseDate],
      ['Purity', lot.purity],
      ['Identity', lot.identity],
      ['Accession number', lot.accessionNumber],
      ['Search code', lot.searchCode],
      ['Lab received date', lot.labReceivedDate],
      ['Reported date', lot.reportedDate],
      ['Net content / composition', lot.netContent],
      ['Additional tests', lot.additionalTests && typeof lot.additionalTests === 'object' ? Object.entries(lot.additionalTests).map(([name, result]) => `${name}: ${result}`).join(' · ') : lot.additionalTests],
      ['Appearance', lot.appearance],
      ['Solubility', lot.solubility],
      ['Storage', lot.storage],
      ['Testing laboratory', lot.testingLab],
      ['Testing country', lot.testingCountry],
      ['Testing standard', lot.testingStandard],
      ['Cap color', lot.capColor],
      ['Crimp color', lot.crimpColor],
      ['Public notes', lot.publicNotes],
    ].filter(([, value]) => String(value || '').trim());

    const renderDetails = (lot) => {
      clearNode(detailsList);
      fieldRows(lot).forEach(([label, value]) => {
        const row = document.createElement('div');
        const dt = document.createElement('dt');
        const dd = document.createElement('dd');
        dt.append(text(label));
        dd.append(text(value));
        row.append(dt, dd);
        detailsList.append(row);
      });
    };

    const renderDocuments = (lot) => {
      clearNode(documentsList);
      const docs = Array.isArray(lot.documents) ? lot.documents.filter((doc) => doc && doc.url) : [];
      documentsEmpty.hidden = docs.length > 0;
      documentsList.hidden = docs.length === 0;
      docs.forEach((doc) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'coa-lookup__document';
        link.href = doc.url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.append(text(doc.label));
        item.append(link);
        documentsList.append(item);
      });
    };

    const statusPriority = (status) => normalize(status) === 'current' ? 0 : 1;

    const renderRelated = (lot) => {
      clearNode(relatedList);
      const related = lots
        .filter((candidate) => candidate.productId && candidate.productId === lot.productId)
        .sort((a, b) => {
          const statusDelta = statusPriority(a.status) - statusPriority(b.status);
          if (statusDelta) return statusDelta;
          return normalize(b.releaseDate).localeCompare(normalize(a.releaseDate));
        });

      const historicalCount = related.filter((candidate) => candidate.id !== lot.id).length;
      relatedEmpty.hidden = historicalCount > 0;
      relatedList.hidden = related.length === 0;

      related.forEach((candidate) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'coa-lookup__related-row';
        button.dataset.lotId = candidate.id;
        button.setAttribute('aria-current', candidate.id === lot.id ? 'true' : 'false');

        [
          [candidate.variantTitle || 'Variant unavailable', 'coa-lookup__related-label'],
          [`Lot ${candidate.lotNumber || 'Unavailable'}`, 'coa-lookup__related-meta'],
          [candidate.status || '', 'coa-lookup__status'],
          [candidate.releaseDate || 'Release date unavailable', 'coa-lookup__related-meta'],
          [candidate.purity || '', 'coa-lookup__related-meta'],
        ].forEach(([value, className]) => {
          const span = document.createElement('span');
          span.className = className;
          span.append(text(value));
          button.append(span);
        });

        button.addEventListener('click', () => selectLot(candidate.id, false));
        relatedList.append(button);
      });
    };

    function selectLot(lotId, updateInput) {
      const lot = lots.find((candidate) => candidate.id === lotId);
      selectedId = lotId;
      closeSuggestions();

      if (!lot) {
        emptyState.hidden = false;
        selectedPanel.hidden = true;
        setMessage('The selected lot record is unavailable.');
        return;
      }

      if (updateInput && input) input.value = lot.lotNumber || lot.productTitle || '';
      emptyState.hidden = true;
      selectedPanel.hidden = false;
      titleNode.textContent = [lot.productTitle, lot.variantTitle].filter(Boolean).join(' — ') || `Lot ${lot.lotNumber}`;
      renderDetails(lot);
      renderDocuments(lot);
      renderRelated(lot);
      setMessage(`Selected lot ${lot.lotNumber || ''}.`);
    }

    input?.addEventListener('input', () => {
      selectedId = null;
      selectedPanel.hidden = true;
      emptyState.hidden = false;
      matches = searchLots(input.value);
      renderSuggestions();
      if (!input.value.trim()) setMessage('');
    });

    input?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!matches.length) return;
        setActiveSuggestion((activeIndex + 1) % matches.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (!matches.length) return;
        setActiveSuggestion(activeIndex <= 0 ? matches.length - 1 : activeIndex - 1);
      } else if (event.key === 'Enter') {
        if (activeIndex >= 0 && matches[activeIndex]) {
          event.preventDefault();
          selectLot(matches[activeIndex].id, true);
        }
      } else if (event.key === 'Escape') {
        closeSuggestions();
      }
    });

    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) closeSuggestions();
    });

    if (!lots.length) {
      setMessage('');
      emptyState.hidden = false;
      selectedPanel.hidden = true;
    } else {
      const token = new URLSearchParams(window.location.search).get('token');
      if (token) {
        const direct = lots.find((lot) => lot.publicToken === token);
        if (direct) {
          selectLot(direct.id, true);
        } else {
          setMessage('The requested public Lot record is unavailable.');
        }
      }
    }
  });
})();
